import { InputChangeEventDetail } from '@ionic/react';
import { IonInputCustomEvent } from '@ionic/core';
import {
  AppFooter_v2 as AppFooter,
  Button_v2 as Button,
  Container_v2 as Container,
  SolidInput,
  Typography,
  TypographyVariantsEnum,
} from '@mindly/ui-components';
import { useEffect, useMemo, useState } from 'react';
import { useLog, validateEmail } from 'src/shared';

type LanguageWorkInProgressWidgetProps = {
  continueWithDefaultLanguageButtonClick: () => void;
  saveEmailButtonClick: (email: string) => void;
  showSuccessScreen: boolean;
  selectedLanguage: string | null;
};

export const LanguageWorkInProgressWidget = ({
  continueWithDefaultLanguageButtonClick,
  saveEmailButtonClick,
  showSuccessScreen,
  selectedLanguage,
}: LanguageWorkInProgressWidgetProps) => {
  const { logError } = useLog();

  const [email, setEmail] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);

  const germanLocalizedStrings = useMemo(
    () => ({
      heading: 'Wir arbeiten noch an der Einführung dieser Sprache.',
      info: 'Hinterlassen Sie Ihre E-Mail, und wir informieren Sie, sobald Psychotherapeuten hier verfügbar sind. Oder Sie können jetzt schon auf Englisch fortfahren.',
      email_placeholder: 'Ihre E-Mail',
      save_button: 'Speichern',
      or: 'oder',
      continue_english_button: 'Auf Englisch fortfahren',

      success_heading: 'Danke! Sie erfahren als Erste vom Start.',
      success_info:
        'Wir haben Ihre E-Mail gespeichert und informieren Sie, wenn Psychotherapeuten in dieser Sprache verfügbar sind 🙌',
    }),
    []
  );

  const frenchLocalizedStrings = useMemo(
    () => ({
      heading: 'Nous travaillons encore sur le lancement de cette langue.',
      info: 'Laissez votre email et nous vous informerons dès que des psychothérapeutes seront disponibles ici. Ou vous pouvez continuer en anglais dès maintenant.',
      email_placeholder: 'Votre email',
      save_button: 'Enregistrer',
      or: 'ou',
      continue_english_button: 'Continuer en anglais',

      success_heading: 'Merci ! Vous serez les premiers informés du lancement.',
      success_info:
        'Nous avons enregistré votre email et vous informerons lorsque des psychothérapeutes seront disponibles dans cette langue 🙌',
    }),
    []
  );

  const localizedStrings = useMemo(() => {
    if (selectedLanguage === 'de') {
      return germanLocalizedStrings;
    }
    if (selectedLanguage === 'fr') {
      return frenchLocalizedStrings;
    }

    logError(new Error('Selected language is not supported'), { selectedLanguage });
    return germanLocalizedStrings;
  }, [selectedLanguage]);

  const handleOnEmailChange = (event: IonInputCustomEvent<InputChangeEventDetail>) => {
    setEmail(event.target.value?.toString() ?? '');
  };

  const handleOnEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && email) {
      saveEmailButtonClick(email);
    }
  };

  useEffect(() => {
    setIsDisabled(!validateEmail(email));
  }, [email]);

  if (showSuccessScreen) {
    return (
      <>
        <Container>
          <div className="flex flex-col justify-center items-center h-full gap-2">
            <Typography variant={TypographyVariantsEnum.Title24Semi} align="center">
              {localizedStrings.success_heading}
            </Typography>
            <Typography variant={TypographyVariantsEnum.Text15Regular} opacity={50} align="center">
              {localizedStrings.success_info}
            </Typography>
          </div>
        </Container>
        <AppFooter>
          <Button fullWidth size="large" fill="outline" onClick={continueWithDefaultLanguageButtonClick}>
            {localizedStrings.continue_english_button}
          </Button>
        </AppFooter>
      </>
    );
  }

  return (
    <Container>
      <div className="flex flex-col gap-1 mb-5">
        <Typography variant={TypographyVariantsEnum.Title24Semi}>{localizedStrings.heading}</Typography>
        <Typography variant={TypographyVariantsEnum.Text15Regular} opacity={50}>
          {localizedStrings.info}
        </Typography>
      </div>

      <div className="flex flex-col gap-4">
        <SolidInput
          placeholder={localizedStrings.email_placeholder}
          value={email}
          onChange={handleOnEmailChange}
          type="email"
          name="email"
          onKeyDown={handleOnEmailKeyDown}
          itemClassName="rounded-[24px]"
        />
        <Button
          fullWidth
          size="large"
          className="flex-1"
          onClick={() => saveEmailButtonClick(email)}
          disabled={isDisabled}
        >
          {localizedStrings.save_button}
        </Button>
      </div>

      <div className="w-full flex justify-between items-center mt-5 mb-6">
        <div className="w-full h-[1px] bg-borders-divider" />
        <Typography
          className="text-typography-typography-secondary mx-[14px]"
          variant={TypographyVariantsEnum.Text13Regular}
        >
          {localizedStrings.or}
        </Typography>
        <div className="w-full h-[1px] bg-borders-divider" />
      </div>

      <Button fullWidth size="large" fill="outline" onClick={continueWithDefaultLanguageButtonClick}>
        {localizedStrings.continue_english_button}
      </Button>
    </Container>
  );
};
